// src/views/boletos/BoletosList.jsx
import React, { useState, useEffect, useCallback } from "react";
import boletosService      from "../../services/boletosService";
import cobrancasService    from "../../services/cobrancasService";
import EnviarBoletoModal  from "../../components/ui/EnviarBoletoModal";
import ConfirmModal       from "../../components/ui/ConfirmModal";
import Banner             from "../../components/ui/Banner";
import condominiosService from "../../services/condominiosService";
import { useAuth } from "../../contexts/AuthContext";

const fmtM = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtD = d => { try{ return new Date((String(d||"").split("T")[0])+"T12:00:00").toLocaleDateString("pt-BR"); }catch{ return d||"—"; } };
const mesAtual = () => { const n=new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`; };

const STATUS = {
  Gerado:     {background:"var(--bg-highlight)",color:"#1E40AF",label:"Gerado"},
  Registrado: {background:"var(--bg-success-soft)",color:"#166534",label:"Registrado"},
  Pago:       {background:"var(--bg-success-soft)",color:"#166534",label:"Pago ✓"},
  Vencido:    {background:"var(--bg-danger-soft)",color:"#991B1B",label:"Vencido"},
  Cancelado:  {background:"var(--bg-page)",color:"var(--gray-400)",label:"Cancelado"},
  Erro:       {background:"var(--bg-danger-soft)",color:"#991B1B",label:"Erro"},
};

export default function BoletosList() {
  const { condominioAtivo, isAdmin, isGerente } = useAuth();
  const { podeEditar } = useAuth();
  const canEdit = podeEditar("boletos");
  const [aba, setAba]               = useState("controle");
  const [condominios, setCondominios] = useState([]);
  const [idCondo, setIdCondo]       = useState("");
  const [data, setData]             = useState([]);
  const [totalizadores, setTotalizadores] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [pagination, setPagination] = useState({total:0,totalPages:1});

  // Filtros
  const [filtros, setFiltros] = useState({
    competencia:mesAtual(), status:"", idUnidade:"", dtVencIni:"", dtVencFim:"", tpBoleto:"", page:1
  });

  // Seleção para registro em lote
  const [selecionados, setSelecionados] = useState([]);
  // Progresso registro API
  const [registrando, setRegistrando]   = useState(false);
  const [progresso, setProgresso]       = useState({total:0, atual:0, ok:0, erros:0, msgs:[]});

  // Modal enviar
  const [showEnviar, setShowEnviar]       = useState(false);
  const [enviarIds,  setEnviarIds]        = useState([]);

  // Modal cancelar
  const [showCancelar, setShowCancelar] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [motivoCancel, setMotivoCancel] = useState("");
  const [cancelando, setCancelando]     = useState(false);

  // Modal baixa manual
  const [showBaixaManual, setShowBaixaManual] = useState(false);
  const [baixaTarget, setBaixaTarget]         = useState(null);
  const [baixaForm, setBaixaForm]             = useState({dtPagamento:"", vlrPago:""});

  // Modal reemitir
  const [showReemitir, setShowReemitir] = useState(false);
  const [reemitirTarget, setReemitirTarget] = useState(null);
  const [formReemitir, setFormReemitir] = useState({vlrNovo:"", dtVencimento:"", motivo:"Reemissão solicitada"});
  const [reemitindo, setReemitindo]     = useState(false);

  /** Consulta EFÍ/Sicoob e baixa se já pago (POST /cobrancas/boletos/:id/sincronizar-pagamento) */
  const [syncingId, setSyncingId] = useState(null);

  // ConfirmModal genérico (substitui window.confirm para UX consistente)
  const [confirm, setConfirm] = useState(null); // { titulo, mensagem, perigo, onConfirm }
  const askConfirm = (cfg) => new Promise((resolve) => {
    setConfirm({
      ...cfg,
      onConfirm: () => { setConfirm(null); resolve(true); },
      onCancel:  () => { setConfirm(null); resolve(false); },
    });
  });

  // Relatório
  const [relFiltros, setRelFiltros] = useState({competencia:mesAtual(), tpRelatorio:"ambos", dtVencIni:"", dtVencFim:""});
  const [relData, setRelData]       = useState(null);
  const [relLoading, setRelLoading] = useState(false);

  // Sync with navbar condominioAtivo — updates whenever user changes it
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);
  useEffect(() => {
    condominiosService.listar({limit:100}).then(r=>{
      setCondominios(r.data||[]);
    }).catch(()=>{});
  },[]);

  const carregar = useCallback(async () => {
    if(!idCondo) return;
    setLoading(true);
    try {
      const r = await boletosService.listar({idCondominio:idCondo,...filtros,limit:20});
      setData(r.data||[]); setTotalizadores(r.totalizadores||[]); setPagination(r.pagination||{});
    } catch(e){ console.error(e); }
    setLoading(false);
  },[idCondo, filtros]);

  useEffect(()=>{ if(aba==="controle") carregar(); },[carregar, aba]);

  const setF = (f,v) => setFiltros(p=>({...p,[f]:v,page:1}));

  const condoNome = condominios.find(c=>String(c.id)===String(idCondo))?.nmFantasia||"";

  // Totais rápidos
  const totPorStatus = (s) => {
    const t = totalizadores.find(x=>x.status===s);
    return { qtd:t?.qtd||0, vlr:parseFloat(t?.vlrTotal||0) };
  };

  // Seleção
  const podeConsultarPagamentoApi = (b) => {
    if (!b || ["Pago", "Cancelado"].includes(b.status)) return false;
    const eff = b.statusEfetivo || b.status;
    if (eff === "Vencido" || b.status === "Registrado") return true;
    if (b.status === "Gerado" && b.tpRegistro === "API") return true;
    return false;
  };

  const handleSincronizarPagamento = async (b) => {
    setSyncingId(b.id);
    try {
      const r = await cobrancasService.sincronizarPagamentoBoleto(b.id);
      if (r.jaPago) alert(r.message || "Boleto já estava pago.");
      else if (r.pago) alert(r.message || "Pagamento confirmado e baixa registrada.");
      else alert(r.message || "Pagamento ainda não identificado na instituição.");
      await carregar();
    } catch (e) {
      alert(e?.message || "Falha ao consultar o banco.");
    } finally {
      setSyncingId(null);
    }
  };

  const toggleSel = (id) => setSelecionados(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const toggleTodas = () => {
    const aguardando = data.filter(b=>["Gerado","Registrado"].includes(b.status));
    setSelecionados(selecionados.length===aguardando.length ? [] : aguardando.map(b=>b.id));
  };

  // Imprimir
  const handleImprimir = async (id) => {
    try{
      const r=await boletosService.imprimir(id);
      const w=window.open("","_blank","width=820,height=900");
      w.document.write(r.html); w.document.close();
    }catch(e){alert(e.message);}
  };

  // Marcar como Registrado (manual)
  const handleBaixaManual = async () => {
    if (!baixaTarget) return;
    try {
      await cobrancasService.baixaManual(baixaTarget.id, baixaForm);
      alert("✅ Baixa registrada com sucesso!");
      setShowBaixaManual(false); setBaixaTarget(null);
      await carregar();
    } catch(e) { alert(e?.message || "Erro ao registrar baixa."); }
  };

  const handleMarcarRegistrado = async (ids) => {
    const lista = Array.isArray(ids) ? ids : [ids];
    const ok = await askConfirm({
      titulo: "Marcar como Registrado",
      mensagem: `Confirmar marcação de ${lista.length} boleto(s) como Registrado no banco? Use esta opção quando registrar manualmente pelo internet banking.`,
      textoOk: "Marcar registrado",
    });
    if (!ok) return;
    try {
      await Promise.all(lista.map(id => boletosService.marcarRegistrado(id)));
      carregar();
    } catch(e) { alert(e?.message || "Erro ao marcar."); }
  };

  // Registrar via API com progresso
  const handleRegistrarAPI = async () => {
    if(!selecionados.length) return alert("Selecione boletos para registrar.");
    const confirmado = await askConfirm({
      titulo: "Registrar via API",
      mensagem: `Registrar ${selecionados.length} boleto(s) na API do banco? Pode levar alguns segundos por boleto.`,
      textoOk: "Registrar",
    });
    if (!confirmado) return;
    setRegistrando(true);
    setProgresso({total:selecionados.length, atual:0, ok:0, erros:0, msgs:[]});

    // Processa 1 por vez para mostrar progresso
    let sucesso=0, erros=0, msgs=[];
    for(let i=0; i<selecionados.length; i++){
      const id=selecionados[i];
      try{
        const r=await boletosService.registrarAPI([id]);
        const res=r.resultados?.[0];
        if(res?.sucesso){ sucesso++; msgs.push({id, ok:true, msg:`Boleto ${id}: Registrado`}); }
        else { erros++; msgs.push({id, ok:false, msg:`Boleto ${id}: ${res?.erro||"Erro"}`}); }
      }catch(e){ erros++; msgs.push({id, ok:false, msg:`Boleto ${id}: ${e.message}`}); }
      setProgresso({total:selecionados.length, atual:i+1, ok:sucesso, erros, msgs:[...msgs]});
    }
    setSelecionados([]); await carregar();
    setTimeout(()=>setRegistrando(false), 2000);
  };

  // Cancelar
  const handleCancelar = async () => {
    if(!motivoCancel.trim()) return alert("Informe o motivo do cancelamento.");
    setCancelando(true);
    try{
      const r=await boletosService.cancelar(cancelTarget.id, motivoCancel);
      alert(r.message);
      setShowCancelar(false); setCancelTarget(null); setMotivoCancel(""); await carregar();
    }catch(e){alert(e.message);}
    setCancelando(false);
  };

  // Reemitir
  const abrirReemitir = (b) => {
    setReemitirTarget(b);
    setFormReemitir({vlrNovo:b.vlrBoleto||"", dtVencimento:b.dtVencimento||"", motivo:"Reemissão solicitada"});
    setShowReemitir(true);
  };
  const handleReemitir = async () => {
    setReemitindo(true);
    try{
      const r=await boletosService.reemitir(reemitirTarget.id, formReemitir);
      alert(r.message);
      setShowReemitir(false); await carregar();
      // Abre o novo boleto para impressão
      if(r.idBoleto) setTimeout(()=>handleImprimir(r.idBoleto), 500);
    }catch(e){alert(e.message);}
    setReemitindo(false);
  };

  // Relatório
  const gerarRelatorio = async () => {
    if(!idCondo) return;
    setRelLoading(true);
    try{ const r=await boletosService.relatorio({idCondominio:idCondo,...relFiltros}); setRelData(r); }
    catch(e){alert(e.message);}
    setRelLoading(false);
  };

  const imprimirRelatorio = () => {
    if(!relData) return;
    const w=window.open("","_blank","width=900,height=800");
    w.document.write(gerarHtmlRelatorio(relData, relFiltros, condoNome));
    w.document.close();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-file-earmark-text me-2" style={{color:"var(--primary)",fontSize:22}}/>Controle de Boletos</h1>
          <p className="page-subtitle">Gerencie, registre, cancele e reimprima boletos</p>
        </div>
        <div className="d-flex gap-2 align-items-center">

        </div>
      </div>

      {/* Banner informativo: indica tela consolidada com Retorno CNAB + Cobranças */}
      <Banner tipo="info">
        <strong>Dica:</strong> para fluxo completo (gerar cobrança, importar retorno CNAB e relatório de adimplência) use o menu <strong>Cobranças → Controle de Boletos</strong>. Esta tela oferece apenas as ações básicas de listagem/envio.
      </Banner>

      {/* Stats rápidos */}
      {idCondo && (
        <div className="row g-3 mb-3">
          {[
            {s:"Registrado",label:"Registrados",   icon:"bi-check-circle",   cls:"green"},
            {s:"Gerado",    label:"Ag. Registro",  icon:"bi-hourglass-split", cls:"blue"},
            {s:"Pago",      label:"Pagos",          icon:"bi-currency-dollar",cls:"green"},
            {s:"Vencido",   label:"Vencidos",       icon:"bi-exclamation-circle",cls:"orange"},
          ].map(({s,label,icon,cls})=>{
            const t=totPorStatus(s);
            return (
              <div className="col-6 col-md-3" key={s}>
                <div className="stat-card" style={{cursor:"pointer"}} onClick={()=>setF("status",filtros.status===s?"":s)}>
                  <div className={`stat-icon ${cls}`}><i className={`bi ${icon}`}/></div>
                  <div>
                    <div className="stat-value">{t.qtd}</div>
                    <div className="stat-label">{label} • {fmtM(t.vlr)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card">
        {/* Sub-abas */}
        <div style={{borderBottom:"1px solid var(--gray-200)",padding:"0 16px",display:"flex",gap:4}}>
          {[{id:"controle",label:"Controle de Boletos",icon:"bi-list-check"},
            {id:"relatorio",label:"Adimplência",icon:"bi-bar-chart-line"}].map(t=>(
            <button key={t.id} className="btn border-0 d-flex align-items-center gap-1"
              style={{fontSize:13,padding:"12px 14px",borderRadius:0,
                borderBottom:aba===t.id?"2px solid var(--primary)":"2px solid transparent",
                fontWeight:aba===t.id?600:400,color:aba===t.id?"var(--primary)":"var(--gray-500)",background:"transparent"}}
              onClick={()=>setAba(t.id)}>
              <i className={`bi ${t.icon}`}/>{t.label}
            </button>
          ))}
        </div>

        {/* ── CONTROLE ── */}
        {aba==="controle" && (
          <>
            {/* Filtros */}
            <div style={{padding:"10px 16px",borderBottom:"1px solid var(--gray-100)",display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end"}}>
              <div>
                <div style={{fontSize:11,fontWeight:600,color:"var(--gray-500)",marginBottom:2}}>Competência</div>
                <input type="month" className="form-control form-control-sm" style={{width:140}} value={filtros.competencia} onChange={e=>setF("competencia",e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:600,color:"var(--gray-500)",marginBottom:2}}>Status</div>
                <select className="form-select form-select-sm" style={{width:"auto"}} value={filtros.status} onChange={e=>setF("status",e.target.value)}>
                  <option value="">Todos</option>
                  {Object.entries(STATUS).map(([v,s])=><option key={v} value={v}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:600,color:"var(--gray-500)",marginBottom:2}}>Vencimento de</div>
                <input type="date" className="form-control form-control-sm" style={{width:135}} value={filtros.dtVencIni} onChange={e=>setF("dtVencIni",e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:600,color:"var(--gray-500)",marginBottom:2}}>Até</div>
                <input type="date" className="form-control form-control-sm" style={{width:135}} value={filtros.dtVencFim} onChange={e=>setF("dtVencFim",e.target.value)}/>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:600,color:"var(--gray-500)",marginBottom:2}}>Tipo de Boleto</div>
                <select className="form-select form-select-sm" style={{width:"auto"}} value={filtros.tpBoleto} onChange={e=>setF("tpBoleto",e.target.value)}>
                  <option value="">Todos</option>
                  <option value="Normal">Normal</option>
                  <option value="Renegociacao">🔄 Renegociação</option>
                </select>
              </div>
              <div className="mt-auto d-flex gap-1">
                <button className="btn btn-outline-secondary btn-sm" onClick={carregar}><i className="bi bi-arrow-clockwise"/></button>
                {selecionados.length>0 && (
                  <>
                    <button className="btn btn-outline-success btn-sm" 
                  onClick={()=>{
                    const sel = data.filter(b=>b._sel).map(b=>b.id);
                    if(!sel.length) return alert("Selecione ao menos um boleto.");
                    setEnviarIds(sel); setShowEnviar(true);
                  }}>
                      <i className="bi bi-send-fill me-1"/>Enviar Selecionados
                    </button>
                    <button className="btn btn-outline-info btn-sm" 
                      title="Marcar selecionados como Registrado no banco (sem API)"
                      onClick={()=>handleMarcarRegistrado(selecionados)}>
                      <i className="bi bi-bank me-1"/>Reg.Banco ({selecionados.length})
                    </button>
                    {data.filter(b=>selecionados.includes(b.id)&&b.tpRegistro==="API").length>0&&(
                      <button className="btn btn-primary btn-sm" onClick={handleRegistrarAPI} disabled={registrando}>
                        <i className="bi bi-cloud-upload me-1"/>Registrar via API ({selecionados.length})
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Barra de progresso */}
            {registrando && (
              <div style={{padding:"12px 16px",background:"var(--bg-highlight)",borderBottom:"1px solid #BFDBFE"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{fontSize:13,fontWeight:600,color:"var(--primary)"}}>
                    Registrando boletos via API... {progresso.atual}/{progresso.total}
                  </span>
                  <span style={{fontSize:12}}>
                    <span style={{color:"#166534",fontWeight:600}}>{progresso.ok} ✓</span>
                    {progresso.erros>0&&<span style={{color:"#991B1B",fontWeight:600,marginLeft:8}}>{progresso.erros} ✗</span>}
                  </span>
                </div>
                <div style={{background:"var(--bg-highlight)",borderRadius:6,height:8,overflow:"hidden"}}>
                  <div style={{height:"100%",background:"var(--primary)",borderRadius:6,width:`${(progresso.atual/progresso.total)*100}%`,transition:"width .3s"}}/>
                </div>
                <div style={{marginTop:6,maxHeight:80,overflowY:"auto"}}>
                  {progresso.msgs.slice(-5).map((m,i)=>(
                    <div key={i} style={{fontSize:11,color:m.ok?"#166534":"#991B1B"}}>
                      {m.ok?"✓":"✗"} {m.msg}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tabela */}
            <div className="table-responsive">
              <table className="table table-custom">
                <thead>
                  <tr>
                    <th style={{width:32}}>
                      <input type="checkbox"
                        checked={selecionados.length>0 && selecionados.length===data.filter(b=>["Gerado","Registrado"].includes(b.status)).length}
                        onChange={toggleTodas}/>
                    </th>
                    <th>Unidade</th><th>Condômino</th><th>Competência</th>
                    <th>Vencimento</th><th>Nosso Nº</th>
                    <th style={{textAlign:"right"}}>Valor</th>
                    <th>Status</th><th>Tipo</th><th style={{width:170}}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="text-center py-4"><span className="spinner-border spinner-border-sm text-primary me-2"/></td></tr>
                  ) : !idCondo ? (
                    <tr><td colSpan={10} className="text-center py-4" style={{color:"var(--gray-400)"}}>Selecione um condomínio</td></tr>
                  ) : data.length===0 ? (
                    <tr><td colSpan={10} className="text-center py-4" style={{color:"var(--gray-400)"}}>
                      <i className="bi bi-file-earmark-text" style={{fontSize:32,display:"block",marginBottom:8}}/>
                      Nenhum boleto encontrado
                    </td></tr>
                  ) : data.map(b => {
                    const st = STATUS[b.status]||STATUS.Gerado;
                    const isSel = selecionados.includes(b.id);
                    const canSel = ["Gerado","Registrado"].includes(b.status);
                    return (
                      <tr key={b.id} style={{background:isSel?"#EFF6FF":""}}>
                        <td>
                          {canSel && <input type="checkbox" checked={isSel} onChange={()=>toggleSel(b.id)}/>}
                        </td>
                        <td style={{fontWeight:700,color:"var(--primary)"}}>
                          {b.unidade}{b.bloco?`/Bl.${b.bloco}`:""}
                        </td>
                        <td style={{fontSize:12.5,maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {b.nmCondomino||"—"}
                        </td>
                        <td style={{fontSize:12.5}}>
                          {b.tpBoleto==="Renegociacao"
                            ?<span style={{background:"#F5F3FF",color:"#7C3AED",padding:"1px 7px",borderRadius:10,fontSize:11,fontWeight:700,marginRight:5}}>🔄 Acordo</span>
                            :null
                          }
                          {b.competencia||b.descGeracao||"—"}
                        </td>
                        <td style={{fontSize:12.5,color:b.status==="Vencido"?"#991B1B":"",fontWeight:b.status==="Vencido"?700:400}}>
                          {fmtD(b.dtVencimento)}
                          {b.status==="Vencido"&&<div style={{fontSize:10,color:"#991B1B"}}>Vencido</div>}
                        </td>
                        <td style={{fontFamily:"monospace",fontSize:12}}>{b.nossoNumero||"—"}</td>
                        <td style={{textAlign:"right",fontWeight:700,fontSize:13,color:"var(--primary)"}}>
                          {fmtM(b.vlrBoleto)}
                        </td>
                        <td>
                          <span style={{...st,padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600}}>
                            {st.label}
                          </span>
                          {b.retornoBanco&&b.status==="Erro"&&(
                            <div style={{fontSize:10,color:"#991B1B",marginTop:2}}>{b.retornoBanco}</div>
                          )}
                        </td>
                        <td>
                          <span style={{background:"var(--gray-100)",padding:"2px 7px",borderRadius:10,fontSize:11}}>
                            {b.tpRegistro||"—"}
                          </span>
                          {b.qrCodePix&&<div style={{fontSize:10,color:"#166534"}}>✓ PIX</div>}
                        </td>
                        <td>
                          <div className="table-actions">
                            <button className="btn btn-outline-success btn-sm btn-icon" title="Enviar por E-mail/WhatsApp"
                              onClick={e=>{e.stopPropagation();setEnviarIds([b.id]);setShowEnviar(true);}}>
                              <i className="bi bi-send-fill"/>
                            </button>
                            <button className="btn btn-outline-secondary btn-sm btn-icon" title="Imprimir boleto"
                              onClick={()=>handleImprimir(b.id)}>
                              <i className="bi bi-printer"/>
                            </button>
                            {["Gerado","Registrado","Vencido"].includes(b.status)&&(
                              <button className="btn btn-success btn-sm btn-icon"
                                title="Baixa Manual"
                                onClick={e=>{e.stopPropagation();setBaixaTarget(b);setBaixaForm({dtPagamento:new Date().toISOString().split("T")[0],vlrPago:b.vlrBoleto});setShowBaixaManual(true);}}>
                                <i className="bi bi-check-circle"/>
                              </button>
                            )}
                            {podeConsultarPagamentoApi(b) && (
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm btn-icon"
                                title="Consultar na API do banco se já foi pago (EFÍ / Sicoob)"
                                disabled={syncingId === b.id}
                                onClick={(e) => { e.stopPropagation(); handleSincronizarPagamento(b); }}>
                                {syncingId === b.id ? (
                                  <span className="spinner-border spinner-border-sm" role="status"/>
                                ) : (
                                  <i className="bi bi-cloud-download"/>
                                )}
                              </button>
                            )}
                            {b.status==="Gerado"&&(
                              <button className="btn btn-outline-info btn-sm btn-icon" title="Marcar como Registrado no banco"
                                onClick={e=>{e.stopPropagation();handleMarcarRegistrado(b.id);}}>
                                <i className="bi bi-bank"/>
                              </button>
                            )}
                            {["Gerado","Registrado","Vencido"].includes(b.status)&&(
                              <button className="btn btn-outline-danger btn-sm btn-icon" title="Cancelar boleto"
                                onClick={()=>{ setCancelTarget(b); setMotivoCancel(""); setShowCancelar(true); }}>
                                <i className="bi bi-x-circle"/>
                              </button>
                            )}
                            {b.status==="Cancelado"&&(
                              <button className="btn btn-outline-warning btn-sm btn-icon" title="Reemitir boleto"
                                style={{borderColor:"#F59E0B",color:"#B45309"}}
                                onClick={()=>abrirReemitir(b)}>
                                <i className="bi bi-arrow-repeat"/>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pagination.totalPages>1&&(
              <div className="pagination-wrapper">
                <span className="pagination-info">{pagination.total} boleto(s)</span>
                <nav><ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${filtros.page===1?"disabled":""}`}>
                    <button className="page-link" onClick={()=>setF("page",filtros.page-1)}><i className="bi bi-chevron-left"/></button>
                  </li>
                  {Array.from({length:Math.min(pagination.totalPages,5)},(_,i)=>(
                    <li key={i+1} className={`page-item ${filtros.page===i+1?"active":""}`}>
                      <button className="page-link" onClick={()=>setF("page",i+1)}>{i+1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${filtros.page===pagination.totalPages?"disabled":""}`}>
                    <button className="page-link" onClick={()=>setF("page",filtros.page+1)}><i className="bi bi-chevron-right"/></button>
                  </li>
                </ul></nav>
              </div>
            )}
          </>
        )}

        {/* ── RELATÓRIO ADIMPLÊNCIA ── */}
        {aba==="relatorio"&&(
          <div style={{padding:16}}>
            <div className="form-section">
              <div className="form-section-title"><i className="bi bi-funnel"/> Filtros</div>
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">Competência</label>
                  <input type="month" className="form-control" value={relFiltros.competencia}
                    onChange={e=>setRelFiltros(p=>({...p,competencia:e.target.value}))}/>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Vencimento de</label>
                  <input type="date" className="form-control" value={relFiltros.dtVencIni}
                    onChange={e=>setRelFiltros(p=>({...p,dtVencIni:e.target.value}))}/>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Até</label>
                  <input type="date" className="form-control" value={relFiltros.dtVencFim}
                    onChange={e=>setRelFiltros(p=>({...p,dtVencFim:e.target.value}))}/>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Exibir</label>
                  <select className="form-select" value={relFiltros.tpRelatorio}
                    onChange={e=>setRelFiltros(p=>({...p,tpRelatorio:e.target.value}))}>
                    <option value="ambos">Adimplentes + Inadimplentes</option>
                    <option value="adimplentes">Apenas Adimplentes</option>
                    <option value="inadimplentes">Apenas Inadimplentes</option>
                  </select>
                </div>
                <div className="col-md-2 d-flex gap-1">
                  <button className="btn btn-primary flex-fill" onClick={gerarRelatorio} disabled={relLoading||!idCondo}>
                    {relLoading?<span className="spinner-border spinner-border-sm"/>:<><i className="bi bi-bar-chart-line me-1"/>Gerar</>}
                  </button>
                  {relData&&<button className="btn btn-outline-secondary" onClick={imprimirRelatorio} title="Imprimir">
                    <i className="bi bi-printer"/>
                  </button>}
                </div>
              </div>
            </div>

            {relData&&(
              <>
                {/* Resumo */}
                <div className="row g-3 mb-3">
                  {[
                    {label:"% Adimplência",  val:`${relData.resumo.pctAdimplencia}%`,    bg:"#DCFCE7",color:"#166534",icon:"bi-emoji-smile"},
                    {label:"% Inadimplência",val:`${relData.resumo.pctInadimplencia}%`,   bg:"#FEE2E2",color:"#991B1B",icon:"bi-emoji-frown"},
                    {label:"Total Recebido", val:fmtM(relData.resumo.totalPago),          bg:"#DCFCE7",color:"#166534",icon:"bi-check-circle"},
                    {label:"Total em Atraso",val:fmtM(relData.resumo.totalAtualizado),    bg:"#FEE2E2",color:"#991B1B",icon:"bi-exclamation-circle"},
                  ].map(s=>(
                    <div className="col-6 col-md-3" key={s.label}>
                      <div style={{background:s.bg,borderRadius:10,padding:"14px 16px"}}>
                        <div style={{fontSize:22,fontWeight:700,color:s.color}}>{s.val}</div>
                        <div style={{fontSize:12,color:s.color,opacity:.8}}>{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Inadimplentes */}
                {relData.inadimplentes?.length>0&&(
                  <div className="form-section">
                    <div className="form-section-title" style={{color:"#991B1B"}}>
                      <i className="bi bi-exclamation-circle me-2"/>Inadimplentes ({relData.inadimplentes.length})
                    </div>
                    <table className="table table-custom" style={{fontSize:12.5}}>
                      <thead>
                        <tr>
                          <th>Unid.</th><th>Proprietário</th><th>Inquilino</th>
                          <th>Vencimento</th><th>Dias Atraso</th>
                          <th style={{textAlign:"right"}}>Valor Original</th>
                          <th style={{textAlign:"right"}}>Multa</th>
                          <th style={{textAlign:"right"}}>Juros</th>
                          <th style={{textAlign:"right",color:"#991B1B"}}>Total Atualizado</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relData.inadimplentes.map((b,i)=>(
                          <tr key={i} style={{background:b.situacao==="Vencido"?"#FFF5F5":"#FFFBEB"}}>
                            <td style={{fontWeight:700,color:"var(--primary)"}}>{b.unidade}{b.bloco?`/B${b.bloco}`:""}</td>
                            <td>{b.nmProprietario||"—"}</td>
                            <td style={{color:"var(--gray-500)"}}>{b.nmInquilino||"—"}</td>
                            <td style={{color:"#991B1B",fontWeight:600}}>{fmtD(b.dtVencimento)}</td>
                            <td>
                              {b.diasAtraso>0
                                ?<span style={{background:"var(--bg-danger-soft)",color:"#991B1B",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600}}>{b.diasAtraso} dias</span>
                                :<span style={{background:"var(--bg-warning-soft)",color:"#854D0E",padding:"2px 8px",borderRadius:10,fontSize:11}}>Em aberto</span>
                              }
                            </td>
                            <td style={{textAlign:"right"}}>{fmtM(b.vlrBoleto)}</td>
                            <td style={{textAlign:"right",color:"#991B1B"}}>{b.vlrMulta>0?fmtM(b.vlrMulta):"—"}</td>
                            <td style={{textAlign:"right",color:"#991B1B"}}>{b.vlrJuros>0?fmtM(b.vlrJuros):"—"}</td>
                            <td style={{textAlign:"right",fontWeight:700,color:"#991B1B"}}>{fmtM(b.vlrAtualizado)}</td>
                            <td>
                              <span style={{background:b.situacao==="Vencido"?"#FEE2E2":"#FEF9C3",
                                color:b.situacao==="Vencido"?"#991B1B":"#854D0E",
                                padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:600}}>
                                {b.situacao==="Vencido"?"Vencido":"Em Aberto"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{fontWeight:700,background:"#FFF5F5"}}>
                          <td colSpan={5}>TOTAL INADIMPLENTES ({relData.inadimplentes.length})</td>
                          <td style={{textAlign:"right"}}>{fmtM(relData.resumo.totalVencido+relData.resumo.totalAberto)}</td>
                          <td style={{textAlign:"right",color:"#991B1B"}}>{fmtM(relData.inadimplentes.reduce((a,b)=>a+b.vlrMulta,0))}</td>
                          <td style={{textAlign:"right",color:"#991B1B"}}>{fmtM(relData.inadimplentes.reduce((a,b)=>a+b.vlrJuros,0))}</td>
                          <td style={{textAlign:"right",color:"#991B1B"}}>{fmtM(relData.resumo.totalAtualizado)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {/* Adimplentes */}
                {relData.adimplentes?.length>0&&(
                  <div className="form-section">
                    <div className="form-section-title" style={{color:"#166534"}}>
                      <i className="bi bi-check-circle me-2"/>Adimplentes ({relData.adimplentes.length})
                    </div>
                    <table className="table table-custom" style={{fontSize:12.5}}>
                      <thead>
                        <tr>
                          <th>Unid.</th><th>Proprietário</th><th>Vencimento</th>
                          <th>Dt. Pagamento</th><th style={{textAlign:"right"}}>Valor Pago</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relData.adimplentes.map((b,i)=>(
                          <tr key={i}>
                            <td style={{fontWeight:700,color:"var(--primary)"}}>{b.unidade}{b.bloco?`/B${b.bloco}`:""}</td>
                            <td>{b.nmProprietario||"—"}</td>
                            <td>{fmtD(b.dtVencimento)}</td>
                            <td style={{color:"#166534"}}>{fmtD(b.dtPagamento)}</td>
                            <td style={{textAlign:"right",fontWeight:600,color:"#166534"}}>{fmtM(b.vlrPago||b.vlrBoleto)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{fontWeight:700,background:"var(--bg-success-soft)"}}>
                          <td colSpan={4}>TOTAL PAGO ({relData.adimplentes.length})</td>
                          <td style={{textAlign:"right",color:"#166534"}}>{fmtM(relData.resumo.totalPago)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── MODAL CANCELAR ── */}
      {showCancelar&&(
        <div className="modal fade show d-block" style={{background:"rgba(0,0,0,.55)"}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cancelar Boleto</h5>
                <button className="btn-close btn-close-white" onClick={()=>setShowCancelar(false)}/>
              </div>
              <div className="modal-body">
                <div style={{background:"#FFF7ED",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#C2410C"}}>
                  <strong>Unidade {cancelTarget?.unidade}{cancelTarget?.bloco?`/Bl.${cancelTarget.bloco}`:""}</strong> — {fmtM(cancelTarget?.vlrBoleto)} — Vencto: {fmtD(cancelTarget?.dtVencimento)}
                </div>
                <label className="form-label">Motivo do Cancelamento <span className="required-mark">*</span></label>
                <textarea className="form-control" rows={3} value={motivoCancel}
                  onChange={e=>setMotivoCancel(e.target.value)}
                  placeholder="Ex: Boleto duplicado, condômino solicitou, erro no valor..."/>
                <div style={{fontSize:11.5,color:"var(--gray-400)",marginTop:8}}>
                  <i className="bi bi-info-circle me-1"/>O sistema tentará cancelar via API bancária. Se falhar, o administrador deverá cancelar manualmente no internet banking.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={()=>setShowCancelar(false)}>Voltar</button>
                <button className="btn btn-danger" onClick={handleCancelar} disabled={cancelando||!motivoCancel.trim()}>
                  {cancelando?<><span className="spinner-border spinner-border-sm me-2"/>Cancelando...</>:<><i className="bi bi-x-circle me-1"/>Confirmar Cancelamento</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL REEMITIR ── */}
      {showReemitir&&(
        <div className="modal fade show d-block" style={{background:"rgba(0,0,0,.55)"}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reemitir Boleto</h5>
                <button className="btn-close btn-close-white" onClick={()=>setShowReemitir(false)}/>
              </div>
              <div className="modal-body">
                <div style={{background:"var(--bg-highlight)",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:13}}>
                  Reemissão para: <strong>Unidade {reemitirTarget?.unidade}</strong> — {reemitirTarget?.nmCondomino||"—"}
                  <div style={{fontSize:11,color:"var(--gray-400)"}}>Um novo nosso número sequencial será gerado automaticamente.</div>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Valor (R$)</label>
                    <div className="input-group"><span className="input-group-text">R$</span>
                      <input className="form-control" type="number" step="0.01"
                        value={formReemitir.vlrNovo}
                        onChange={e=>setFormReemitir(p=>({...p,vlrNovo:e.target.value}))}/>
                    </div>
                    <small style={{fontSize:11,color:"var(--gray-400)"}}>Original: {fmtM(reemitirTarget?.vlrBoleto)}</small>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Novo Vencimento</label>
                    <input type="date" className="form-control" value={formReemitir.dtVencimento}
                      onChange={e=>setFormReemitir(p=>({...p,dtVencimento:e.target.value}))}/>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Motivo</label>
                    <input className="form-control" value={formReemitir.motivo}
                      onChange={e=>setFormReemitir(p=>({...p,motivo:e.target.value}))}/>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={()=>setShowReemitir(false)}>Cancelar</button>
                <button className="btn btn-warning" onClick={handleReemitir} disabled={reemitindo} style={{color:"#fff"}}>
                  {reemitindo?<><span className="spinner-border spinner-border-sm me-2"/>Reemitindo...</>:<><i className="bi bi-arrow-repeat me-1"/>Reemitir Boleto</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showBaixaManual && baixaTarget && (
        <div className="modal fade show d-block" style={{background:"rgba(0,0,0,.5)"}} tabIndex="-1"
          onClick={()=>setShowBaixaManual(false)}>
          <div className="modal-dialog modal-dialog-centered" style={{maxWidth:420}}
            onClick={e=>e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-check-circle me-2 text-success"/>
                  Baixa Manual — Unidade {baixaTarget.unidade}
                </h5>
                <button className="btn-close" onClick={()=>setShowBaixaManual(false)}/>
              </div>
              <div className="modal-body">
                <p style={{fontSize:13,color:"var(--gray-500)"}}>
                  Boleto: <strong>{fmtM(baixaTarget.vlrBoleto)}</strong> · Venc: <strong>{fmtD(baixaTarget.dtVencimento)}</strong>
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Data do Pagamento</label>
                  <input type="date" className="form-control"
                    value={baixaForm.dtPagamento}
                    onChange={e=>setBaixaForm(f=>({...f,dtPagamento:e.target.value}))}/>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Valor Pago</label>
                  <input type="number" step="0.01" className="form-control"
                    value={baixaForm.vlrPago}
                    onChange={e=>setBaixaForm(f=>({...f,vlrPago:e.target.value}))}/>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={()=>setShowBaixaManual(false)}>Cancelar</button>
                <button className="btn btn-success" onClick={handleBaixaManual}>
                  <i className="bi bi-check-circle me-1"/>Confirmar Baixa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEnviar && (
        <EnviarBoletoModal
          ids={enviarIds}
          onClose={()=>setShowEnviar(false)}
          onSent={()=>{ setTimeout(()=>setShowEnviar(false), 2000); carregar(); }}
        />
      )}

      {confirm && (
        <ConfirmModal
          titulo={confirm.titulo}
          mensagem={confirm.mensagem}
          perigo={!!confirm.perigo}
          textoOk={confirm.textoOk || "Confirmar"}
          textoCancelar={confirm.textoCancelar || "Cancelar"}
          onConfirm={confirm.onConfirm}
          onCancel={confirm.onCancel}
        />
      )}
    </div>
  );
}

// ── HTML do relatório para impressão ─────────────────────────────────────
function gerarHtmlRelatorio(relData, filtros, condoNome) {
  const fmtM = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
  const fmtD = d => { try{ return new Date((String(d||"").split("T")[0])+"T12:00:00").toLocaleDateString("pt-BR"); }catch{ return d||"—"; } };
  const r = relData.resumo;
  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <title>Relatório de Adimplência — ${condoNome}</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:12px;padding:20px;color:#333}
      h2{color:#1B3A6B;border-bottom:2px solid #1B3A6B;padding-bottom:8px}
      table{width:100%;border-collapse:collapse;margin-bottom:20px}
      th{background:#1B3A6B;color:#fff;padding:6px 8px;text-align:left}
      td{padding:5px 8px;border-bottom:1px solid #eee}
      .r{text-align:right} .green{color:#166534} .red{color:#991B1B}
      .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px}
      .card{border-radius:6px;padding:12px;text-align:center}
      .card .val{font-size:20px;font-weight:700} .card .lbl{font-size:11px;opacity:.8}
      tfoot td{font-weight:700;background:#f8f8f8}
      @media print{.np{display:none}}
    </style></head><body>
    <div class="np" style="text-align:center;margin-bottom:16px">
      <button onclick="window.print()" style="background:#1B3A6B;color:#fff;border:none;padding:8px 20px;border-radius:4px;cursor:pointer;margin-right:8px">🖨️ Imprimir</button>
      <button onclick="window.close()" style="background:#64748B;color:#fff;border:none;padding:8px 20px;border-radius:4px;cursor:pointer">✕ Fechar</button>
    </div>
    <h2>Relatório de Adimplência — ${condoNome}</h2>
    <p style="color:#666;margin-bottom:16px">Competência: <strong>${filtros.competencia||"—"}</strong> • Emitido em: ${new Date().toLocaleString("pt-BR")}</p>
    <div class="summary">
      <div class="card" style="background:#DCFCE7"><div class="val green">${r.pctAdimplencia}%</div><div class="lbl green">Adimplência</div></div>
      <div class="card" style="background:#FEE2E2"><div class="val red">${r.pctInadimplencia}%</div><div class="lbl red">Inadimplência</div></div>
      <div class="card" style="background:#DCFCE7"><div class="val green">${fmtM(r.totalPago)}</div><div class="lbl green">Total Recebido</div></div>
      <div class="card" style="background:#FEE2E2"><div class="val red">${fmtM(r.totalAtualizado)}</div><div class="lbl red">Total em Atraso</div></div>
    </div>
    ${relData.inadimplentes?.length>0?`
    <h3 style="color:#991B1B">Inadimplentes (${relData.inadimplentes.length})</h3>
    <table>
      <thead><tr><th>Unidade</th><th>Proprietário</th><th>Vencimento</th><th>Dias</th><th class="r">Original</th><th class="r">Multa</th><th class="r">Juros</th><th class="r">Atualizado</th></tr></thead>
      <tbody>${relData.inadimplentes.map(b=>`
        <tr>
          <td><strong>${b.unidade}${b.bloco?`/B${b.bloco}`:""}</strong></td>
          <td>${b.nmProprietario||"—"}</td>
          <td class="red">${fmtD(b.dtVencimento)}</td>
          <td>${b.diasAtraso>0?`${b.diasAtraso}d`:"Em aberto"}</td>
          <td class="r">${fmtM(b.vlrBoleto)}</td>
          <td class="r red">${b.vlrMulta>0?fmtM(b.vlrMulta):"—"}</td>
          <td class="r red">${b.vlrJuros>0?fmtM(b.vlrJuros):"—"}</td>
          <td class="r red"><strong>${fmtM(b.vlrAtualizado)}</strong></td>
        </tr>`).join("")}
      </tbody>
      <tfoot><tr><td colspan="4">TOTAL</td><td class="r">${fmtM(r.totalVencido+r.totalAberto)}</td><td class="r red">${fmtM(relData.inadimplentes.reduce((a,b)=>a+b.vlrMulta,0))}</td><td class="r red">${fmtM(relData.inadimplentes.reduce((a,b)=>a+b.vlrJuros,0))}</td><td class="r red">${fmtM(r.totalAtualizado)}</td></tr></tfoot>
    </table>`:""}
    ${relData.adimplentes?.length>0?`
    <h3 style="color:#166534">Adimplentes (${relData.adimplentes.length})</h3>
    <table>
      <thead><tr><th>Unidade</th><th>Proprietário</th><th>Vencimento</th><th>Dt. Pagamento</th><th class="r">Valor Pago</th></tr></thead>
      <tbody>${relData.adimplentes.map(b=>`
        <tr>
          <td><strong>${b.unidade}${b.bloco?`/B${b.bloco}`:""}</strong></td>
          <td>${b.nmProprietario||"—"}</td>
          <td>${fmtD(b.dtVencimento)}</td>
          <td class="green">${fmtD(b.dtPagamento)}</td>
          <td class="r green"><strong>${fmtM(b.vlrPago||b.vlrBoleto)}</strong></td>
        </tr>`).join("")}
      </tbody>
      <tfoot><tr><td colspan="4">TOTAL</td><td class="r green">${fmtM(r.totalPago)}</td></tr></tfoot>
    </table>`:""}
    <div style="text-align:center;font-size:10px;color:#999;margin-top:20px">Horizon eCondo — ${new Date().toLocaleString("pt-BR")}</div>
    </body></html>`;
}
